/**
 * The pro-ladder switch, on the bar above the panel it switches.
 *
 * It began in the account menu, which is where the other per-device
 * preferences live. It belongs beside the thing it changes instead: a mode
 * buried two clicks into an identity menu is one most people never find, and
 * one nobody can turn *off* without remembering where it came from. It sits
 * **above** the panel header rather than inside it so that it reads as a
 * control over the whole column rather than as a property of whichever panel
 * happens to be there — which is the honest description, since switching it
 * replaces the panel.
 *
 * It renders on both bars — the order ticket's and the ladder's — so the
 * gesture is reversible from wherever it left you. That is the whole reason it
 * is a shared component rather than two chips.
 *
 * **It is `<Button>` at `size="sm"`, the header's own Connect button.** A
 * bespoke chip here would be a second answer to "what does a small pressable
 * thing look like", and the two would drift. `variant` carries the state:
 * `primary` is the filled accent every armed control in this product uses, and
 * `secondary` is the same button unlit.
 *
 * `role="switch"` on top of it: a `role` is a promise about the keyboard
 * (invariant 20), and the promise a switch makes is that Enter and Space toggle
 * it and that its state is readable. `<Button>` is a native `<button>`, so both
 * come for free — which is why this is not a new primitive.
 */

import { Button } from '@/components/primitives'
import { useStore } from '@/app/use-store'
import { setProMode } from '@/app/preferences'
import { uiStore } from '@/stores/ui'

export interface ProToggleProps {
  className?: string
}

export function ProToggle({ className }: ProToggleProps) {
  // Subscribed here rather than passed down. `useStore` is
  // `useSyncExternalStore`, so this subscription hoisted into `<TradeRoute>`
  // would re-render the whole market page — and `uiStore` is written by a
  // gesture, which is exactly the kind of change a route must not be
  // downstream of (invariant 2).
  const on = useStore(uiStore).proMode

  return (
    <Button
      variant={on ? 'primary' : 'secondary'}
      size="sm"
      role="switch"
      aria-checked={on}
      aria-label="Pro ladder"
      title={on ? 'Back to the order ticket' : 'One clickable ladder instead of the ticket'}
      onClick={() => setProMode(!on)}
      {...(className ? { className } : {})}
    >
      PRO
    </Button>
  )
}
